import styled, { css } from 'styled-components'
import { match } from 'ts-pattern'

import { Tooltip } from '@blade/ui-kit'
import { CssUtil } from '@root/shared/core'
import { useTranslation } from '@root/shared/localization'
import { mediaMaxDesktopSmall } from '@root/shared/ui-kit'

import { PairAudit } from '../../model'
import { AUDIT_STATUS_ICON_MAP, auditToColor, auditToLabel } from '../constants'

import type { ClassNameProps } from '@root/shared/react'

type Props = {
  audit: PairAudit.Audit
  onAuditClick?: () => void
} & ClassNameProps

export const PairAuditBadge: React.FC<Props> = ({
  audit,
  className,
  onAuditClick,
}) => {
  const { t } = useTranslation()

  const totalChecks = PairAudit.totalChecks(audit)
  const rankLabel = t(auditToLabel(audit.overAllChecks.overall))
  const Icon = AUDIT_STATUS_ICON_MAP[audit.overAllChecks.overall]

  return (
    <Tooltip content={rankLabel}>
      <Root
        className={className}
        onClick={onAuditClick}
        $status={audit.overAllChecks.overall}
      >
        <Icon size={14} />
        <Counter>
          {audit.overAllChecks.safeCount}/{totalChecks}
        </Counter>
      </Root>
    </Tooltip>
  )
}

const Root = styled.div<{
  $status: PairAudit.AuditStatus | undefined
}>`
  --bg-color: ${({ $status, theme }) =>
    match($status)
      .with(undefined, () => '#7B7B7B33')
      .with('Safe', () => CssUtil.hexToRgbA(theme.palette.success[9], 0.2))
      .with('Warning', () => CssUtil.hexToRgbA(theme.palette.warning[9], 0.2))
      .with('Danger', () => CssUtil.hexToRgbA(theme.palette.error[9], 0.2))
      .exhaustive()};

  color: ${({ $status }) =>
    $status ? auditToColor($status) : 'var(--text-10)'};
  cursor: pointer;

  display: grid;
  grid-template-columns: auto auto auto;
  gap: 2px;
  align-items: center;
`

const Counter = styled.span`
  display: inline-block;
  text-align: center;
  font-size: 11px;
  line-height: 1;
  font-weight: 700;
  padding-inline: 6px;

  border-radius: 10px;
  background: var(--bg-color);

  margin-inline-start: 2px;

  padding: 2px 5px;

  ${mediaMaxDesktopSmall(css`
    font-size: 10px;
  `)}
`
